import React from "react";
import classNames from "classnames/bind";
import { Field, FieldProps, FieldRenderProps } from "react-final-form";
import useSWR from "swr";

import { CATEGORIES } from "../../api/categories";

import { MobileSelect } from "components/MobileSelect";
import { Input } from "components/Input";
import { RadioGroup } from "components/RadioGroup";

import { useStateManager } from "utils/useStateManager";

import { required } from "./validation";

import styles from "./AnthropometryFields.module.css";

const cx = classNames.bind(styles);

type Props = {
  isChild: boolean;
};

export enum Smoking {
  YES = "YES",
  NO = "NO",
}

export type FormValues = {
  custom_масса_тела: string;
  custom_рост: string;
  custom_детская_жизненная_активность?: string;
  custom_жизненная_активность?: string;
  custom_спортивная_автивность?: string;
  smoking: Smoking;
};

type FProps = FieldProps<
  string,
  FieldRenderProps<string, HTMLElement>,
  HTMLElement
>;

type FormFieldProps = React.ComponentProps<typeof Input> & {
  name: keyof FormValues;
  format?: FProps["format"];
  validate?: FProps["validate"];
};

const FormField: React.FC<FormFieldProps> = (props) => (
  <Field name={props.name} format={props.format} validate={props.validate}>
    {({ input, meta }) => (
      <Input
        {...input}
        label={props.label}
        required={props.required}
        error={meta.touched ? meta.error : undefined}
      />
    )}
  </Field>
);

export const AnthropometryFields: React.FC<Props> = (props) => {
  const { linkInfo } = useStateManager();
  const isChild = props.isChild;
  const { data: sport = [] } = useSWR<
    {
      key: string;
      custom_наименование: string;
      custom_описание: string;
    }[]
  >([CATEGORIES.ACTIVITY_SPORT, 29]);
  const { data: life = [] } = useSWR<
    {
      key: string;
      custom_наименование: string;
      custom_описание: string;
    }[]
  >([CATEGORIES.ACTIVITY_LIFE, isChild ? 1837 : 1836]);

  return (
    <>
      <fieldset className={cx("fieldset")}>
        <FormField
          validate={required}
          format={(value) => (value ? value.replace(".", ",") : value)}
          name="custom_масса_тела"
          label="Масса тела"
        />
        <FormField
          validate={required}
          format={(value) => (value ? value.replace(".", ",") : value)}
          name="custom_рост"
          label="Рост, см"
        />
      </fieldset>
      <fieldset className={cx("fieldset")}>
        <Field
          validate={required}
          name={
            isChild
              ? "custom_детская_жизненная_активность"
              : "custom_жизненная_активность"
          }
          render={({ input }) => (
            <MobileSelect
              options={life.map((item) => ({
                value: item.key,
                label: item.custom_наименование,
                description: item.custom_описание,
              }))}
              placeholder="Жизненная активность"
              {...input}
            />
          )}
        />
        {isChild || (
          <Field
            validate={required}
            name="custom_спортивная_автивность"
            render={({ input }) => (
              <MobileSelect
                options={sport.map((item) => ({
                  value: item.key,
                  label: item.custom_наименование,
                  description: item.custom_описание,
                }))}
                placeholder="Спортивная активность"
                {...input}
              />
            )}
          />
        )}
      </fieldset>
      {linkInfo?.custom?.custom_есть_вопрос_о_курении_в_саморегистрации && (
        <fieldset className={cx("fieldset")}>
          <div className={cx("select")}>
            <Field validate={required} name="smoking">
              {(props) => (
                <RadioGroup
                  name={props.input.name}
                  label="Курите ли вы?"
                  items={[
                    {
                      label: "Да",
                      value: Smoking.YES,
                    },
                    {
                      label: "Нет",
                      value: Smoking.NO,
                    },
                  ]}
                  onChange={(
                    event: React.ChangeEvent<HTMLInputElement>
                  ) => {
                    props.input.onChange(event.target.value);
                  }}
                  value={props.input.value}
                />
              )}
            </Field>
          </div>
        </fieldset>
      )}
    </>
  );
};
